import { supabase } from './supabase';
import { embedOne } from './utils';
import { isConversationUnread } from './conversationSeen';
import type { Message } from '../types';

export type HostTripStage = 'inquiry' | 'building' | 'confirmed' | 'active' | 'past';

export interface HostConversationSummary {
  matchId: string;
  conversationId: string;
  travelerId: string;
  travelerName: string | null;
  travelerAvatar: string | null;
  lastMessage: Message | null;
  updatedAt: string;
  unread: boolean;
  /** Trip name (or destination) for the row's status line. */
  tripName: string | null;
  /** Pipeline stage — drives the status dot, header line, and filters. */
  stage: HostTripStage;
  /** Date range label, e.g. "Jul 5–8". */
  dates: string | null;
  /** "Day X of Y" when the trip is live. */
  dayOf: string | null;
}

/** Stage → status-dot colour (matches the dashboard pipeline language). */
export const STAGE_DOT_COLOR: Record<HostTripStage, string> = {
  inquiry: 'var(--text-dim)',
  building: 'rgb(var(--violet))',
  confirmed: 'rgb(var(--gold))',
  active: 'rgb(var(--success))',
  past: 'rgb(var(--text-dim-rgb) / .6)',
};

export const STAGE_LABEL: Record<HostTripStage, string> = {
  inquiry: 'New inquiry',
  building: 'Building itinerary',
  confirmed: 'Confirmed',
  active: 'On trip now',
  past: 'Completed',
};

/** Same stage semantics as the dashboard pipeline. */
export function deriveHostStage(matchStatus: string, itineraryStatus: string | null, start: Date | null, end: Date | null, now: Date): HostTripStage {
  // Paid and waiting on the host. An unpaid shortlist entry ('proposed') never reaches the host.
  if (matchStatus === 'pending') return 'inquiry';
  if (matchStatus === 'completed' || (end && end < now)) return 'past';
  if (start && end && start <= now && end >= now) return 'active';
  if (itineraryStatus === 'approved') return 'confirmed';
  return 'building';
}

export function formatRange(start: Date | null, end: Date | null): string | null {
  if (!start || !end) return null;
  const s = start.toLocaleDateString('en', { month: 'short', day: 'numeric' });
  const e = end.toLocaleDateString('en', start.getMonth() === end.getMonth() ? { day: 'numeric' } : { month: 'short', day: 'numeric' });
  return `${s}–${e}`;
}

export { getLastSeen, markConversationSeen, isConversationUnread } from './conversationSeen';

/**
 * Loads all of the host's conversations (matches in pending/accepted/completed,
 * same status filter as Dashboard.tsx) joined with their conversation thread and
 * traveler profile, sorted by most recent activity.
 */
export async function fetchHostConversations(hostId: string): Promise<HostConversationSummary[]> {
  const { data: matchRows } = await supabase
    .from('matches')
    .select(`
      id,
      traveler_id,
      status,
      traveler_profile:profiles!matches_traveler_id_fkey(full_name, avatar_url),
      conversation:conversations(id, messages, updated_at),
      itinerary:itineraries(status, content)
    `)
    .eq('host_id', hostId)
    .in('status', ['pending', 'accepted', 'completed'])
    .order('created_at', { ascending: false });

  if (!matchRows) return [];

  const now = new Date();

  const summaries: HostConversationSummary[] = matchRows.map((row) => {
    const raw = row as Record<string, unknown>;
    const tp = raw.traveler_profile as { full_name: string | null; avatar_url: string | null } | null;
    const convRows = raw.conversation as Array<{ id: string; messages: Message[]; updated_at: string }> | null;
    const conv = convRows?.[0] ?? null;
    const messages = conv?.messages ?? [];
    const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null;

    const itin = embedOne(raw.itinerary as { status: string; content: Record<string, unknown> | null } | { status: string; content: Record<string, unknown> | null }[] | null);
    const content = itin?.content ?? null;
    const tripName = content ? String(content.trip_name ?? '') || String(content.destination ?? '') || null : null;
    const start = content?.start_date ? new Date(String(content.start_date)) : null;
    const end = content?.end_date ? new Date(String(content.end_date)) : null;
    const stage = deriveHostStage(raw.status as string, itin?.status ?? null, start, end, now);

    let dayOf: string | null = null;
    if (stage === 'active' && start && end) {
      const total = Math.round((end.getTime() - start.getTime()) / 86400000) + 1;
      const day = Math.min(Math.floor((now.getTime() - start.getTime()) / 86400000) + 1, total);
      dayOf = `Day ${day} of ${total}`;
    }

    return {
      matchId: raw.id as string,
      conversationId: conv?.id ?? '',
      travelerId: raw.traveler_id as string,
      travelerName: tp?.full_name ?? null,
      travelerAvatar: tp?.avatar_url ?? null,
      lastMessage,
      updatedAt: conv?.updated_at ?? lastMessage?.timestamp ?? '',
      unread: conv?.id ? isConversationUnread(conv.id, lastMessage, hostId) : false,
      tripName,
      stage,
      dates: formatRange(start, end),
      dayOf,
    };
  });

  return summaries
    .filter((s) => s.conversationId)
    .sort((a, b) => (b.updatedAt || '').localeCompare(a.updatedAt || ''));
}
